import { useState } from "react";
import { Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/cr/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* body already consumed */ }
      if (!res.ok) throw new Error(data.detail || "Invalid credentials");
      onLogin(data.token);
    } catch (err) {
      setError(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "#0b1120" }}
      data-testid="admin-login-page"
    >
      {/* Subtle grid overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative w-full max-w-sm">
        <div
          className="rounded-2xl border p-8"
          style={{ background: "#111827", borderColor: "#1e293b" }}
        >
          {/* Icon + title */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5"
              style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}
            >
              <Lock size={20} className="text-blue-400" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">
              Adiora Control Room
            </h1>
            <p className="text-sm mt-1" style={{ color: "#64748b" }}>
              Superadmin access only
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="admin-email"
                className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                style={{ color: "#94a3b8" }}
              >
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-3.5 py-2.5 rounded-lg text-sm text-white placeholder-gray-600 border outline-none transition-colors"
                style={{
                  background: "#1e293b",
                  borderColor: "#334155",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.target.style.borderColor = "#334155")}
                data-testid="admin-login-email"
              />
            </div>

            <div>
              <label
                htmlFor="admin-password"
                className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                style={{ color: "#94a3b8" }}
              >
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="w-full px-3.5 py-2.5 rounded-lg text-sm text-white placeholder-gray-600 border outline-none transition-colors"
                style={{
                  background: "#1e293b",
                  borderColor: "#334155",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.target.style.borderColor = "#334155")}
                data-testid="admin-login-password"
              />
            </div>

            {error && (
              <div
                className="text-sm px-4 py-2.5 rounded-lg"
                style={{ background: "rgba(239,68,68,0.1)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-all mt-2"
              style={{
                background: loading ? "#1d4ed8" : "#2563eb",
                opacity: loading ? 0.7 : 1,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = "#1d4ed8")}
              onMouseLeave={(e) => !loading && (e.target.style.background = "#2563eb")}
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-6" style={{ color: "#334155" }}>
          Adiora Internal — not accessible to learners
        </p>
      </div>
    </div>
  );
}
